import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import {
  DateTime,
  DateTimeRegion,
  NaiveDate,
  TimezoneRegion,
  Tzname,
  Utc,
} from "@tento-chrono";
import { ZoneLoad } from "@tento-lona/sheets/internal-sdk";
import { Tracing } from "@tento-core/tracing";
import { deepFrozen } from "@tento-lona/sheets/internal-sdk";

/**
 * What `FlightInfo.fromEvents` reads off an event: its UTC start and end, its
 * timezones and its title.
 */
export interface FlightEvent {
  st: Option<DateTime<Utc>>;
  et: Option<DateTime<Utc>>;
  /** The timezone the event states outright (`null`: none; `suggestedTimezone` is read next). */
  timezone: Option<string>;
  suggestedTimezone: Option<string>;
  t: Option<string>;
}

/**
 * A flight: its departure and arrival as wall-clock times in their regions,
 * with optional app data.
 */
export class FlightInfo<Info = null> {
  readonly range: DateTime.Range<Utc>;

  constructor(
    readonly start: DateTimeRegion /* WALL CLOCK */,
    readonly end: DateTimeRegion /* WALL CLOCK */,
    readonly info: Option<Info>,
  ) {
    this.range = new DateTime.Range(start.asWallClock(), end.asWallClock());
  }

  get startDate(): NaiveDate {
    return this.range.start.date;
  }

  toString(): string {
    return `${this.start.toString()} -> ${this.end.toString()}`;
  }
}

/**
 * Types and values that go with `FlightInfo`: `Serialized`, `debugPrint`,
 * `fromTransitions`, `fromEvents`, `fromSerialized`, `SERIALIZED_DEBUG`, and 1
 * more.
 */
export namespace FlightInfo {
  export type Serialized = {
    start: string;
    startTz: string;
    end: string;
    endTz: string;
  };

  export function debugPrint<E extends FlightEvent>(flightinfos: FlightInfo<E>[]) {
    for (const fi of flightinfos) {
      Tracing.dev(fi.info?.t);
      Tracing.dev(
        "  ",
        fi.start.toString(),
        fi.start.tz.toWallClock(fi.start.ndt).toString(),
      );
      Tracing.dev(
        "  ",
        fi.end.toString(),
        fi.end.tz.toWallClock(fi.end.ndt).toString(),
      );
      Tracing.dev(fi);
    }
  }

  export async function fromTransitions<E extends { start: string; end: string; timezone: string }>(
    transitions: E[],
    defaultTimezone: Tzname,
  ): Promise<FlightInfo<E>[]> {
    const sorted = [...transitions].sort((a, b) => {
      return (
        nonnull(DateTime.utc.parse(a.start), "FlightInfo: transition start").mse -
        nonnull(DateTime.utc.parse(b.start), "FlightInfo: transition start").mse
      );
    });

    const uniqueTzs = new Set<string>([defaultTimezone]);
    for (const t of sorted) uniqueTzs.add(t.timezone);

    const tzEntries = await Promise.all(
      [...uniqueTzs].map(async (tz) => [tz, await ZoneLoad.orElse(tz, Tzname.UTC, "flight")] as [string, TimezoneRegion]),
    );
    const tzCache = new Map(tzEntries);

    const result: FlightInfo<E>[] = [];
    let lastTz: Option<string> = null;

    for (const t of sorted) {
      const st = DateTime.utc.parse(t.start);
      const et = DateTime.utc.parse(t.end);
      if (!st || !et) continue;

      const startTz = lastTz ?? defaultTimezone;
      const endTz = t.timezone;

      const startRegion = tzCache.get(startTz);
      const endRegion = tzCache.get(endTz);
      if (!startRegion || !endRegion) continue;

      const startTransition = startRegion.activeTransition(st.mse);
      const endTransition = endRegion.activeTransition(et.mse);
      if (!startTransition || !endTransition) continue;

      result.push(
        new FlightInfo(
          new DateTimeRegion(st.toTz(startTransition.after.time.tz).ndt, startRegion),
          new DateTimeRegion(et.toTz(endTransition.after.time.tz).ndt, endRegion),
          t,
        ),
      );

      lastTz = endTz;
    }

    return result;
  }

  export async function fromEvents<E extends FlightEvent>(
    events: E[],
    defaultTimezone: Tzname,
  ): Promise<FlightInfo<E>[]> {
    // Sort events by start time (ascending - oldest first, most recent last)
    const sortedEvents = [...events].sort((a, b) => {
      if (!a.st || !b.st) return 0;
      return a.st.mse - b.st.mse;
    });

    // Collect all unique timezone names we'll need
    const uniqueTimezones = new Set<string>();
    uniqueTimezones.add(defaultTimezone);

    for (const event of sortedEvents) {
      const eventTz = event.timezone ?? event.suggestedTimezone;
      if (eventTz) {
        uniqueTimezones.add(eventTz);
      }
    }

    // Pre-load all timezone regions in parallel
    const timezonePromises = Array.from(uniqueTimezones).map(async (tz) => {
      const region = await ZoneLoad.orElse(tz, Tzname.UTC, "flight");
      return [tz, region] as [string, TimezoneRegion];
    });

    const timezoneEntries = await Promise.all(timezonePromises);
    const timezoneCache = new Map(timezoneEntries);

    const result: FlightInfo<E>[] = [];
    let lastEndTime: Option<number> = null;
    let lastEventTz: Option<string> = null;

    for (const e of sortedEvents) {
      if (!e.st || !e.et) continue;

      // Skip overlapping events
      if (lastEndTime && e.st.mse < lastEndTime) continue;

      const eventTz = e.timezone ?? e.suggestedTimezone;
      if (!eventTz) continue;

      // For end timezone: use current event's timezone
      const endTz = eventTz;

      // For start timezone: use previous valid event's timezone if available, otherwise use default
      const startTz = lastEventTz ?? defaultTimezone;

      // Get timezone regions from cache
      const startTzRegion = timezoneCache.get(startTz);
      const endTzRegion = timezoneCache.get(endTz);

      if (!startTzRegion || !endTzRegion) continue;

      // Find the active timezone transition at the UTC time to get the correct offset
      const startTransition = startTzRegion.activeTransition(e.st.mse);
      const endTransition = endTzRegion.activeTransition(e.et.mse);

      if (!startTransition || !endTransition) continue;

      // Convert UTC times to local times using the correct timezone offsets
      const localStart = e.st.toTz(startTransition.after.time.tz);
      const localEnd = e.et.toTz(endTransition.after.time.tz);

      result.push(
        new FlightInfo(
          new DateTimeRegion(localStart.ndt, startTzRegion),
          new DateTimeRegion(localEnd.ndt, endTzRegion),
          e,
        ),
      );

      lastEndTime = e.et.mse;
      lastEventTz = eventTz;
    }

    return result;
  }

  export async function fromSerialized(s: Serialized): Promise<FlightInfo> {
    const [startTz, endTz] = await Promise.all([
      ZoneLoad.orElse(s.startTz, Tzname.UTC, "flight"),
      ZoneLoad.orElse(s.endTz, Tzname.UTC, "flight"),
    ]);

    // Parse UTC times
    const utcStart = DateTime.fromRfc3339(s.start).exp();
    const utcEnd = DateTime.fromRfc3339(s.end).exp();

    // Find the active timezone transition at the UTC time to get the correct offset
    const startTransition = startTz.activeTransition(utcStart.mse);
    const endTransition = endTz.activeTransition(utcEnd.mse);

    if (!startTransition || !endTransition) {
      throw new Error("No timezone transition found for the given times");
    }

    // Convert UTC times to local times using the correct timezone offsets
    const localStart = utcStart.toTz(startTransition.after.time.tz);
    const localEnd = utcEnd.toTz(endTransition.after.time.tz);

    return new FlightInfo(
      new DateTimeRegion(localStart.ndt, startTz),
      new DateTimeRegion(localEnd.ndt, endTz), null,
    );
  }

  export const SERIALIZED_DEBUG: readonly Serialized[] = deepFrozen([
    {
      start: "2024-06-22T23:10:00Z",
      startTz: "America/Los_Angeles",
      end: "2024-06-23T14:40:00Z",
      endTz: "Asia/Qatar",
    },
    {
      start: "2024-06-24T04:45:00Z",
      startTz: "Asia/Qatar",
      end: "2024-06-24T11:50:00Z",
      endTz: "Asia/Bangkok",
    },
    {
      start: "2024-07-02T14:00:00Z",
      startTz: "Asia/Bangkok",
      end: "2024-07-02T16:55:00Z",
      endTz: "Asia/Makassar",
    },
    {
      start: "2024-07-06T09:40:00Z",
      startTz: "Asia/Makassar",
      end: "2024-07-06T11:30:00Z",
      endTz: "Asia/Singapore",
    },
    {
      start: "2024-07-08T00:45:00Z",
      startTz: "Asia/Singapore",
      end: "2024-07-08T16:15:00Z",
      endTz: "America/Los_Angeles",
    },
  ]);
  let _debug: Option<FlightInfo[]>;

  export async function debug(): Promise<FlightInfo[]> {
    if (_debug != null) return _debug;
    return (_debug = await Promise.all(SERIALIZED_DEBUG.map(fromSerialized)));
  }
}
